/**
 * Session storage that never keeps Google's OAuth tokens.
 *
 * After a Google sign-in GoTrue returns the session with Google's own
 * `provider_token` (a Google access token) and, when granted,
 * `provider_refresh_token`. supabase-js persists the whole session object, so
 * without this adapter both would sit in `localStorage` for the life of the
 * session. IMAM never uses them: Google only authenticates, the IMAM session is
 * the GoTrue access and refresh token pair, and the Drive Picker asks Google
 * Identity Services for its own short-lived token.
 *
 * Only the session entry under `sessionKey` is rewritten. Other keys the client
 * stores beside it (PKCE code verifiers) pass through untouched. A session
 * stored before this adapter existed is scrubbed the first time it is read,
 * which the client does while initialising.
 */

const PROVIDER_TOKEN_FIELDS = ["provider_token", "provider_refresh_token"] as const;

export type SessionStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** The stored JSON without provider tokens, or `null` when nothing needs to change. */
function withoutProviderTokens(value: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const session = parsed as Record<string, unknown>;
  if (!PROVIDER_TOKEN_FIELDS.some((field) => field in session)) return null;
  for (const field of PROVIDER_TOKEN_FIELDS) delete session[field];
  return JSON.stringify(session);
}

export function providerTokenFreeStorage(storage: SessionStorage, sessionKey: string): SessionStorage {
  return {
    getItem(key) {
      const value = storage.getItem(key);
      if (key !== sessionKey || value === null) return value;
      const scrubbed = withoutProviderTokens(value);
      if (scrubbed === null) return value;
      storage.setItem(key, scrubbed);
      return scrubbed;
    },
    setItem(key, value) {
      storage.setItem(key, key === sessionKey ? (withoutProviderTokens(value) ?? value) : value);
    },
    removeItem(key) {
      storage.removeItem(key);
    },
  };
}
